import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import { catalogPrimaryButtonSx } from "../components/Products/catalogGridStyles.js";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import useApiData from "../services/useApiData.js";
import api from "../services/axiosInterceptor.js";
import {
    AgingDistribution,
    BreakdownPanel,
    ChannelTable,
    KpiCard,
    KpiGrid,
    ReportContent,
    ReportError,
    ReportLoading,
    ReportNote,
    ReportScroll,
    ReportSection,
    ReportSplit,
    ReportToolbar,
    SpotlightStrip,
    formatCurrency,
    formatNumber,
} from "../components/Reporting/ReportPrimitives.jsx";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultLaborRange() {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    return { from: dayInputValue(start), to: dayInputValue(end) };
}

function formatLaborDollars(value) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value));
}

function formatLaborSummary(result) {
    if (!result) return "";
    const sessions = formatNumber(result.sessionsProcessed, 0);
    const items = formatNumber(result.itemsUpdated, 0);
    const labor = formatLaborDollars(result.totalLaborAllocated);
    const parts = [
        `Allocated ${labor} across ${items} catalog items from ${sessions} clock sessions (${result.from} – ${result.to}).`,
    ];
    if (result.skippedOpenShifts > 0) {
        parts.push(`${formatNumber(result.skippedOpenShifts, 0)} open shift(s) skipped.`);
    }
    if (result.sessionsWithNoItems > 0) {
        parts.push(
            `${formatNumber(result.sessionsWithNoItems, 0)} session(s) had no matching items.`
        );
    }
    return parts.join(" ");
}

export default function ReportingInventory() {
    const { data, error, load, refetch } = useApiData("api/broylesllc/reports/inventory");
    const initialLabor = defaultLaborRange();
    const [from, setFrom] = useState(initialLabor.from);
    const [to, setTo] = useState(initialLabor.to);
    const [allocating, setAllocating] = useState(false);
    const [allocResult, setAllocResult] = useState(null);
    const [allocError, setAllocError] = useState("");

    const onHand = data?.onHand;
    const inTransit = data?.inTransit;
    const aging = data?.aging;

    const statusRows = useMemo(() => {
        const byStatus = inTransit?.byStatus || {};
        return Object.entries(byStatus).map(([channel, stats]) => ({
            channel,
            key: channel,
            ...stats,
        }));
    }, [inTransit]);

    const supplierRows = useMemo(() => {
        const bySupplier = inTransit?.bySupplier || {};
        return Object.entries(bySupplier)
            .map(([channel, stats]) => ({
                channel,
                key: channel,
                ...stats,
            }))
            .sort((a, b) => (b.value || 0) - (a.value || 0))
            .slice(0, 8);
    }, [inTransit]);

    const totalTracked = (onHand?.value || 0) + (inTransit?.value || 0);
    const onHandShare =
        totalTracked > 0 ? Math.round(((onHand?.value || 0) / totalTracked) * 100) : null;

    const allocateLabor = async (event) => {
        event.preventDefault();
        setAllocating(true);
        setAllocError("");
        setAllocResult(null);
        try {
            const { data: result } = await api.post("/api/broylesllc/reports/allocate-labor", {
                from,
                to,
            });
            setAllocResult(result);
            await refetch();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to allocate labor costs.";
            setAllocError(message);
        } finally {
            setAllocating(false);
        }
    };

    return (
        <CatalogPageShell
            title="Inventory"
            subtitle="Capital sitting on shelves, capital still in transit, and how long it has aged."
        >
            <ReportScroll>
                <form onSubmit={allocateLabor}>
                    <ReportToolbar>
                        <PullDateRangeFields
                            layout="row"
                            startDate={from}
                            endDate={to}
                            onStartChange={setFrom}
                            onEndChange={setTo}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            size="small"
                            disabled={allocating || !from || !to}
                            startIcon={
                                allocating ? <CircularProgress size={14} color="inherit" /> : null
                            }
                            sx={catalogPrimaryButtonSx}
                        >
                            {allocating ? "Allocating…" : "Allocate labor"}
                        </Button>
                        <div className="report-toolbar-spacer" />
                        <div className="report-toolbar-meta">
                            Spread timesheet Gross pay onto items created the same Eastern day as clock-in
                        </div>
                    </ReportToolbar>
                </form>

                {allocError ? <ReportError message={allocError} /> : null}
                {allocResult ? <ReportNote>{formatLaborSummary(allocResult)}</ReportNote> : null}

                {error ? <ReportError message="Failed to load inventory report." /> : null}

                {load && !data ? (
                    <ReportLoading label="Rolling up inventory value…" />
                ) : data ? (
                    <ReportContent>
                        <SpotlightStrip
                            items={[
                                {
                                    label: "On hand",
                                    value: formatCurrency(onHand?.value),
                                    hint: `${formatNumber(onHand?.units, 0)} shelved units`,
                                },
                                {
                                    label: "In transit",
                                    value: formatCurrency(inTransit?.value),
                                    hint: `${formatNumber(inTransit?.invoiceCount, 0)} open invoices`,
                                },
                                {
                                    label: "Total tracked",
                                    value: formatCurrency(totalTracked),
                                    hint:
                                        onHandShare != null
                                            ? `${onHandShare}% currently on hand`
                                            : "On-hand + in-transit",
                                },
                            ]}
                        />

                        <ReportSection
                            eyebrow="Capital"
                            title="Inventory value"
                            subtitle="On-hand is shelved and unsold. In-transit is purchase invoices still open."
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="On-hand value"
                                    value={formatCurrency(onHand?.value)}
                                    hint={`${formatNumber(onHand?.units, 0)} units`}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="In-transit value"
                                    value={formatCurrency(inTransit?.value)}
                                    hint={`${formatNumber(inTransit?.invoiceCount, 0)} invoices`}
                                />
                                <KpiCard
                                    label="In-transit units"
                                    value={formatNumber(inTransit?.units, 0)}
                                    hint="From invoice rollups"
                                />
                                <KpiCard
                                    label="On-hand share"
                                    value={onHandShare != null ? `${onHandShare}%` : "—"}
                                    hint="Of total tracked value"
                                />
                            </KpiGrid>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Freshness"
                            title="Aging status"
                            subtitle="How long on-hand inventory has sat since purchase — fresher capital turns faster."
                        >
                            <AgingDistribution buckets={aging?.buckets} />
                            {(aging?.missingPurchaseDate?.units || 0) > 0 ? (
                                <ReportNote>
                                    {formatNumber(aging.missingPurchaseDate.units, 0)} on-hand
                                    items ({formatCurrency(aging.missingPurchaseDate.value)}) are
                                    missing a purchase date and are excluded from aging.
                                </ReportNote>
                            ) : null}
                        </ReportSection>

                        <ReportSection
                            eyebrow="Pipeline"
                            title="In-transit breakdown"
                            subtitle="Open invoices awaiting pickup or already shipped to you."
                        >
                            <ReportSplit>
                                <BreakdownPanel title="By delivery status">
                                    <ChannelTable
                                        rows={statusRows}
                                        emptyLabel="No in-transit invoices right now."
                                        columns={[
                                            {
                                                key: "channel",
                                                label: "Status",
                                            },
                                            {
                                                key: "invoiceCount",
                                                label: "Invoices",
                                                align: "right",
                                                muted: true,
                                                render: (row) =>
                                                    formatNumber(row.invoiceCount, 0),
                                            },
                                            {
                                                key: "value",
                                                label: "Value",
                                                align: "right",
                                                primary: true,
                                                render: (row) => formatCurrency(row.value),
                                            },
                                        ]}
                                    />
                                </BreakdownPanel>
                                <BreakdownPanel title="Top suppliers">
                                    <ChannelTable
                                        rows={supplierRows}
                                        emptyLabel="No supplier pipeline to show."
                                        columns={[
                                            {
                                                key: "channel",
                                                label: "Supplier",
                                            },
                                            {
                                                key: "invoiceCount",
                                                label: "Invoices",
                                                align: "right",
                                                muted: true,
                                                render: (row) =>
                                                    formatNumber(row.invoiceCount, 0),
                                            },
                                            {
                                                key: "value",
                                                label: "Value",
                                                align: "right",
                                                primary: true,
                                                render: (row) => formatCurrency(row.value),
                                            },
                                        ]}
                                    />
                                </BreakdownPanel>
                            </ReportSplit>
                        </ReportSection>
                    </ReportContent>
                ) : null}
            </ReportScroll>
        </CatalogPageShell>
    );
}
